// 開課/修改課程大 modal（course-clazz-list「新增課程／修改課程」＝legacy `#dialogModal`）。
//
// legacy 流程（行號親驗 2026-09-22；詳 docs/plans/2026-09-22-batch11-course-modal-progress.md §1）：
//   - showModal(id)（clazzList_js.jsp:439-617）：新增＝.newShow 顯示（循環排課/樣版）、刪除鈕藏、
//     kind 預設 'class'（:546-548）、code label「由系統自動產生」（clazzList_js.jsp:505）；修改＝.newHide、
//     prefill currentRow（name/code/kind/caseType/qty/attendanceQty/visitingInstructor/classDate/
//     beginTime/endTime/actionType split/isSignUp/isCrossDomain/attendanceRole 勾回、
//     attendanceList→學員右欄 :587-603、classTeacherList→教師清單 :605-614）、showFormControl()
//     ＋refreshClassForm()（:615-616，編輯模式開表單控制區）。
//   - save/doSave（:2196-2317）：`#inputForm`.valid()（inline jquery validate，不過不送）→
//     validForSave（:2177-2208）組字 alert（請選擇醫院〔admin〕/請選擇訓練類別/請選擇部門/
//     至少選擇一名授課教師/請輸入正確的起訖區間!，`<br>` 串接）→ POST course/clazz/saveClassAjax
//     （attendanceRole 組字、studentIds JSON、weekLoops JSON、refJobTypeId 去主類別去重、content）→
//     成功 queryClass＋重開 modal（多筆排課則關 modal）＋info。
//   - delClass :358-386：modal 內刪除 → confirm『是否確定要刪除？』（全形？）→ 刪除後關 modal＋reload＋info。
//
// 雛形資料策略（照 RolePermissionModal／批次 10 AccountV2DialogModal 先例）：
//   - 存檔走既有 `dataSource.create/update('course-clazz-list',…)`；子清單（學員/教師/教材/表單）
//     存列上（attendanceList/classTeacherList/attachFiles/classForms；course-clazz-query-list
//     的 attendance/forms 種子先例），寫回用 update op。
//   - 真呼叫 DataSource 的按鈕才帶 data-effect（C5-j）；表單控制區的 checkbox/input 變更也是
//     真寫回（update op），但不帶 data-effect（屬性語意是按鈕自報，非按鈕元件不標，差異記進度檔）。
//   - 無真後端：CKEDITOR→textarea、日期/時間→文字輸入、檔案不落地（名稱/大小入清單）、
//     ajax 級聯下拉→種子池平列（clazzList_js.jsp:758-1611 initInputForm 簡化）、
//     匯入名單 checkMemberLisAjax 無真檔案服務（守衛文案照鏡像，成功訊息為雛形文字）——
//     全部標【雛形值待實機校準】。
//   - 多筆循環排課（classDates×weekLoops 產生多筆課程）雛形存單筆（weekLoops 照存欄位，
//     不產生多筆）【雛形值待實機校準】。
//
// 【證據等級】§7 第 3 級（原始碼）。文案 codepoint 表見進度檔 §1.3。

import { useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn, type TableRow } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { MockDataError, resolveLegacyMessage, useDataSource } from '@/mocks';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_JOB_TYPE_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
  COURSE_CLAZZ_ACTION_TYPE_OPTIONS,
  COURSE_CLAZZ_ANNUAL_OPTIONS,
  COURSE_CLAZZ_CASE_TYPE_OPTIONS,
  COURSE_CLAZZ_COURSE_TYPE_OPTIONS,
  COURSE_CLAZZ_DEVICE_OPTIONS,
  COURSE_CLAZZ_FORM_TEMPLATE_OPTIONS,
  COURSE_CLAZZ_KIND_OPTIONS,
  COURSE_CLAZZ_LOCATION_DEPT_OPTIONS,
  COURSE_CLAZZ_LOCATION_OPTIONS,
  COURSE_CLAZZ_PHASE_OPTIONS,
  COURSE_CLAZZ_PLAN_TARGET_OPTIONS,
  COURSE_CLAZZ_STUDENT_POOL,
  COURSE_CLAZZ_TEACHER_OPTIONS,
  COURSE_CLAZZ_TEMPLATE_OPTIONS,
} from '@/mocks/resource-rules';
import type { Row } from '@/mocks/types';
import { toUiKey } from '@/pages/templates/shared';

export interface ClassEditModalProps {
  readonly pageId: string;
  /** 'create'＝新增（showModal(0)）；'edit'＝修改（showClass → showModal(row.id)）。 */
  readonly mode: 'create' | 'edit';
  /** 修改時的列（currentRow；子清單種子掛在列上）。 */
  readonly row: Row | null;
  readonly onClose: () => void;
  readonly onClosed: () => void;
}

interface CoreFormState {
  kind: string;
  organizationId: string;
  jobTypeId: string;
  refJobTypeIds: readonly string[];
  isCrossDomain: boolean;
  departmentId: string;
  courseDeptId: string;
  courseId: string;
  courseTypeId: string;
  caseType: string;
  name: string;
  classDates: string;
  classDate: string;
  weeks: readonly boolean[];
  beginTime: string;
  endTime: string;
  isSignUp: string;
  signUpPeriod: string;
  attendanceQty: string;
  qty: string;
  locationDeptId: string;
  locationId: string;
  teacherDeptId: string;
  teacherId: string;
  visitingInstructor: string;
  formOwnerDeptId: string;
  formOwnerId: string;
  actionTypes: readonly string[];
  devices: readonly string[];
  testContent: string;
}

interface TeacherEntry {
  readonly id: string;
  readonly name: string;
  readonly teacherType: 'instructor' | 'teamTeacher';
}

interface StudentEntry {
  readonly id: string;
  readonly name: string;
}

/** 與會人員區的計畫學員表單（6 欄）。 */
interface PlanFormState {
  stuAnnualId: string;
  stuJobTypeId: string;
  stuDepartmentId: string;
  planTargetId: string;
  phaseDeptId: string;
  phaseYM: string;
}

/** 週籤（legacy week0~week6 value 7,1,2,3,4,5,6＝週日~週六，clazzList.jsp:385-397）。 */
const WEEK_LABELS: readonly string[] = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
const WEEK_VALUES: readonly string[] = ['7', '1', '2', '3', '4', '5', '6'];

/** 依角色選取的 4 個角色（clazzList.jsp:716-723 value=UGY/PGY/R/VS）。 */
const ATTENDANCE_ROLES: readonly { value: string; label: string }[] = [
  { value: 'UGY', label: 'UGY' },
  { value: 'PGY', label: 'PGY' },
  { value: 'R', label: '住院醫師' },
  { value: 'VS', label: '主治醫師' },
];

const emptyCore = (mode: 'create' | 'edit', row: Row | null): CoreFormState => ({
  kind: mode === 'edit' ? String(row?.['kind'] ?? 'class') : 'class', // :546-548
  organizationId: '',
  jobTypeId: '',
  refJobTypeIds: [],
  isCrossDomain: false,
  departmentId: '',
  courseDeptId: '',
  courseId: '',
  courseTypeId: '',
  caseType: mode === 'edit' ? String(row?.['caseType'] ?? '1') : '1', // :551
  name: mode === 'edit' ? String(row?.['title'] ?? '') : '', // 名稱欄 key=title（表格欄）
  classDates: '',
  classDate: mode === 'edit' ? String(row?.['classDate'] ?? '') : '',
  weeks: [false, false, false, false, false, false, false],
  beginTime: mode === 'edit' ? String(row?.['beginTime'] ?? '') : '',
  endTime: mode === 'edit' ? String(row?.['endTime'] ?? '') : '',
  isSignUp: mode === 'edit' ? String(row?.['isSignUp'] ?? '0') : '0',
  signUpPeriod: mode === 'edit' ? String(row?.['signUpPeriod'] ?? '') : '',
  attendanceQty: mode === 'edit' ? String(row?.['attendanceQty'] ?? '1') : '1', // :456 value="1"
  qty: mode === 'edit' ? String(row?.['qty'] ?? '1') : '1', // :464 value="1"
  locationDeptId: '',
  locationId: '',
  teacherDeptId: '',
  teacherId: '',
  visitingInstructor: mode === 'edit' ? String(row?.['visitingInstructor'] ?? '') : '',
  formOwnerDeptId: '',
  formOwnerId: '',
  actionTypes: [],
  devices: [],
  testContent: '',
});

const listOn = (row: Row | null, key: string): readonly Row[] => {
  const raw = row?.[key];
  return Array.isArray(raw) ? (raw as readonly Row[]) : [];
};

export function ClassEditModal({
  pageId,
  mode,
  row,
  onClose,
  onClosed,
}: ClassEditModalProps): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const closeMainModal = () => {
    onClose();
    onClosed();
  };
  const saveSuccessText = () =>
    resolveLegacyMessage(pageId, 'success')?.text ?? t('state.saveSuccess');
  const alertOrGeneric = async (cause: unknown) => {
    await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
  };

  // ------------------------------------------------------------------
  // 主表單
  // ------------------------------------------------------------------
  const [core, setCore] = useState<CoreFormState>(() => emptyCore(mode, row));
  const [busy, setBusy] = useState(false);
  /** 帳號 id（legacy oId）：新增＝'0'，存檔後回填新列 id（:2244-2251）。 */
  const [classId, setClassId] = useState(mode === 'edit' ? String(row?.id ?? '') : '0');

  const setCoreValue = <K extends keyof CoreFormState>(key: K, next: CoreFormState[K]) => {
    setCore((current) => ({ ...current, [key]: next }));
  };

  // ------------------------------------------------------------------
  // 子清單：教師／教材／學員（雙 select）／表單控制區
  // ------------------------------------------------------------------
  const [teachers, setTeachers] = useState<readonly TeacherEntry[]>(() =>
    listOn(row, 'classTeacherList').map((entry) => ({
      id: String(entry['teacherId'] ?? ''),
      name: String(entry['teacherName'] ?? ''),
      teacherType: Number(entry['type'] ?? 1) === 1 ? ('instructor' as const) : ('teamTeacher' as const),
    })),
  );
  const [attachFiles, setAttachFiles] = useState<readonly Row[]>(() => listOn(row, 'attachFiles'));
  const [students, setStudents] = useState<readonly StudentEntry[]>(() =>
    listOn(row, 'attendanceList').map((entry) => ({
      id: String(entry['studentId'] ?? ''),
      name: String(entry['studentName'] ?? '') + (entry['employeeId'] ? `(${String(entry['employeeId'])})` : ''),
    })),
  );
  const [classForms, setClassForms] = useState<readonly Row[]>(() => listOn(row, 'classForms'));
  /** 雙 select 的選取狀態（legacy multiselect.js 內部狀態的 React 鏡像；移動後清空）。 */
  const [pickedPool, setPickedPool] = useState<readonly string[]>([]);
  const [pickedTo, setPickedTo] = useState<readonly string[]>([]);
  const [formControlOpen, setFormControlOpen] = useState(mode === 'edit'); // :615-616 編輯模式開
  const [formDeptId, setFormDeptId] = useState('');
  const [formTemplateId, setFormTemplateId] = useState('');
  const [formDoneDateDiff, setFormDoneDateDiff] = useState('3'); // :892 value="3"
  const [formDispatchType, setFormDispatchType] = useState(false);
  const [formParticipantRead, setFormParticipantRead] = useState(false);

  // ------------------------------------------------------------------
  // 與會人員區（changeAddMemberType :2606-2637）
  // ------------------------------------------------------------------
  const [addMemberType, setAddMemberType] = useState('2'); // :260 預設依計畫挑選人員（checked）
  const [roleChecks, setRoleChecks] = useState<ReadonlySet<string>>(() => {
    // 修改模式：row.attendanceRole（逗號串）勾回（:558-569）
    const raw = mode === 'edit' ? String(row?.['attendanceRole'] ?? '') : '';
    return new Set(raw.split(',').filter((value) => value !== ''));
  });
  const [accountKeyword, setAccountKeyword] = useState('');
  /** 增加人員區的 accountId select（legacy clazzList.jsp:688；keyword 過濾選項＝setAnyAccountList 鏡像）。 */
  const [accountIdSelected, setAccountIdSelected] = useState('');
  const accountIdOptions = COURSE_CLAZZ_STUDENT_POOL.filter(
    (option) => accountKeyword.trim() === '' || option.label.includes(accountKeyword.trim()),
  );
  const [planForm, setPlanForm] = useState<PlanFormState>(() => ({
    stuAnnualId: '',
    stuJobTypeId: '',
    stuDepartmentId: '',
    planTargetId: '',
    phaseDeptId: '',
    phaseYM: '',
  }));

  // ------------------------------------------------------------------
  // 教師（genTeacherDiv :1680-1735）
  // ------------------------------------------------------------------
  const addTeacher = (teacherType: 'instructor' | 'teamTeacher') => {
    void (async () => {
      const teacherId = core.teacherId;
      if (teacherId === '') {
        return; // legacy id==null 直接 return（:1682-1684）
      }
      const name =
        COURSE_CLAZZ_TEACHER_OPTIONS.find((option) => option.value === teacherId)?.label ?? teacherId;
      const instructor = teachers.find((entry) => entry.teacherType === 'instructor');
      if (teacherType === 'instructor') {
        if (instructor !== undefined) {
          await dialogs.alert('授課教師僅能為一名'); // :1702
          return;
        }
        if (teachers.some((entry) => entry.id === teacherId && entry.teacherType === 'teamTeacher')) {
          await dialogs.alert('此教師已為協同教師'); // :1707
          return;
        }
      } else {
        if (instructor !== undefined && instructor.id === teacherId) {
          await dialogs.alert('此教師已為授課教師'); // :1716
          return;
        }
      }
      if (teachers.some((entry) => entry.id === teacherId && entry.teacherType === teacherType)) {
        return; // :1720-1726 isExist 不重複加入
      }
      setTeachers((current) => [...current, { id: teacherId, name, teacherType }]);
    })();
  };

  const removeTeacher = (id: string, teacherType: 'instructor' | 'teamTeacher') => {
    setTeachers((current) => current.filter((entry) => !(entry.id === id && entry.teacherType === teacherType)));
  };

  // ------------------------------------------------------------------
  // 教材（initClassAttachFileTable :2460-2505；檔案不落地【雛形值待實機校準】）
  // ------------------------------------------------------------------
  const [newAttachName, setNewAttachName] = useState('');
  const addAttachFile = (fileName: string) => {
    if (fileName.trim() === '') {
      return;
    }
    setAttachFiles((current) => [
      ...current,
      { fileId: `file-new-${current.length + 1}`, name: fileName, size: '待上傳' },
    ]);
    setNewAttachName('');
  };
  const deleteAttachFile = (fileId: string) => {
    setAttachFiles((current) => current.filter((entry) => String(entry['fileId']) !== fileId));
  };

  // ------------------------------------------------------------------
  // 學員雙 select（initStudentSelect :652-757）
  // ------------------------------------------------------------------
  /** 全部右移（beforeMoveToRight 上限檢查 :666-696；afterMoveToRight toast :708-745）。 */
  const moveStudentsRightAll = () => {
    void (async () => {
      const candidates = COURSE_CLAZZ_STUDENT_POOL.filter(
        (option) => !students.some((entry) => entry.id === option.value),
      );
      const willAmount = students.length + candidates.length;
      const limit = Number(core.attendanceQty);
      if (willAmount > limit) {
        await dialogs.alert('超過上課人數.'); // :673（002E）
        return;
      }
      setStudents((current) => [
        ...current,
        ...candidates.map((option) => ({ id: option.value, name: option.label })),
      ]);
      setPickedPool([]);
      if (row !== null) {
        await dialogs.info('需將課程存檔後，下方表單的填寫者清單才會顯示正確的人員!'); // :2165-2171（FF0C＋0021）
      }
    })();
  };

  const moveSelectedRight = (pickedIds: readonly string[]) => {
    void (async () => {
      const limit = Number(core.attendanceQty);
      const willAmount = students.length + pickedIds.length;
      if (pickedIds.length > 0 && willAmount > limit) {
        await dialogs.alert('超過上課人數.'); // :673
        return;
      }
      const duplicated = pickedIds.filter((id) => students.some((entry) => entry.id === id));
      if (duplicated.length > 0) {
        const names = duplicated
          .map((id) => COURSE_CLAZZ_STUDENT_POOL.find((option) => option.value === id)?.label ?? id)
          .join(',');
        await dialogs.alert(`${names}已存在.`); // :691（existMsg＋已存在.）
        return;
      }
      if (pickedIds.length === 0) {
        return;
      }
      setStudents((current) => [
        ...current,
        ...pickedIds.map((id) => ({
          id,
          name: COURSE_CLAZZ_STUDENT_POOL.find((option) => option.value === id)?.label ?? id,
        })),
      ]);
      setPickedPool([]);
      if (row !== null) {
        await dialogs.info('需將課程存檔後，下方表單的填寫者清單才會顯示正確的人員!'); // afterMoveToRight
      }
    })();
  };

  const moveSelectedLeft = (pickedIds: readonly string[]) => {
    void (async () => {
      if (pickedIds.length === 0) {
        return;
      }
      setStudents((current) => current.filter((entry) => !pickedIds.includes(entry.id)));
      setPickedTo([]);
      if (row !== null) {
        await dialogs.info('需將課程存檔後，下方表單的填寫者清單才會顯示正確的人員!'); // afterMoveToLeft
      }
    })();
  };

  /** 修課學員池左欄（池扣除已選）。 */
  const studentPool = COURSE_CLAZZ_STUDENT_POOL.filter(
    (option) => !students.some((entry) => entry.id === option.value),
  );

  // ------------------------------------------------------------------
  // 與會人員（addAnyMemberList :2526-2605／addAnyStudent :2144-2164／selectAllRole :2757-2766）
  // ------------------------------------------------------------------
  const [importFileName, setImportFileName] = useState('');
  const importMemberList = () => {
    void (async () => {
      // legacy checkMemberLisAjax（:2536-2542）；雛形無真檔案服務，守衛文案照鏡像、
      // 匯入結果以雛形完成訊息呈現【雛形值待實機校準】。
      if (importFileName.trim() === '') {
        await dialogs.alert('未選擇檔案!'); // :2529-2531（0021）
        return;
      }
      await dialogs.processing.wrap(new Promise<void>((resolve) => setTimeout(resolve, 0)));
      await dialogs.info('名單檢核完成');
    })();
  };

  const addAnyStudent = () => {
    void (async () => {
      // legacy addAnyStudent（:2144-2164）讀 #accountId option:selected；keyword 僅供 setAnyAccountList
      // 過濾選項（:402-407 initModal）。雛形：accountId select 選項＝學員池依 keyword 過濾。
      if (accountIdSelected.trim() === '') {
        return;
      }
      if (students.some((entry) => entry.id === accountIdSelected)) {
        await dialogs.alert('人員已經存在!'); // :2153（0021）
        return;
      }
      const label =
        COURSE_CLAZZ_STUDENT_POOL.find((option) => option.value === accountIdSelected)?.label ?? accountIdSelected;
      setStudents((current) => [...current, { id: accountIdSelected, name: label }]);
      // legacy 不在此處 reload 表單（註解 // refreshClassForm()）；提示 toast。
      await dialogs.info('需將課程存檔後，下方表單的填寫者清單才會顯示正確的人員!');
    })();
  };

  const selectAllRole = (check: boolean) => {
    setRoleChecks(check ? new Set(ATTENDANCE_ROLES.map((role) => role.value)) : new Set());
  };

  // ------------------------------------------------------------------
  // 表單控制區（refreshClassForm :2102-2143／formSave :2326-2360／delTarget :2432-2450）
  // ------------------------------------------------------------------
  const formSave = () => {
    void (async () => {
      if (formTemplateId === '') {
        await dialogs.alert('未選擇表單!'); // :2328（0021）
        return;
      }
      const next: readonly Row[] = [
        ...classForms,
        {
          formTargetId: `target-new-${classForms.length + 1}`,
          formTemplateId,
          formName:
            COURSE_CLAZZ_FORM_TEMPLATE_OPTIONS.find((option) => option.value === formTemplateId)?.label ??
            formTemplateId,
          doneDateDiff: formDoneDateDiff,
          dispatchType: formDispatchType ? 0 : 1, // :2338（checked→0）
          isParticipantRead: formParticipantRead ? 1 : 0, // :2339
          // :2340 studentIds＝$('.student:checked')（僅勾選者；首次存檔無核取＝空）。
          formList: [],
        },
      ];
      setBusy(true);
      try {
        // legacy POST course/clazz/saveClassFormTargetAjax（:2343）→ refreshClassForm；
        // 雛形：classForms 存列上（update 既有 op）。
        await dialogs.processing.wrap(dataSource.update(pageId, classId, { classForms: next }));
        setClassForms(next);
        // legacy 存檔後刻意不清空表單下拉（jsp:2341-2342 註解掉 //$("#formTemplateId").val('')），照實保留選值。
        await dialogs.info(saveSuccessText());
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  const deleteFormTarget = (formTargetId: string) => {
    void (async () => {
      const confirmed = await dialogs.confirm('是否確定要刪除?'); // :2433（003F）
      if (!confirmed) {
        return;
      }
      const next = classForms.filter((entry) => String(entry['formTargetId']) !== formTargetId);
      setBusy(true);
      try {
        await dialogs.processing.wrap(dataSource.update(pageId, classId, { classForms: next }));
        setClassForms(next);
        await dialogs.info(saveSuccessText());
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  /** 表單控制區的列上寫回（student checkbox／dispatchType／isParticipantRead 變更即寫，:2361-2449）。 */
  const patchFormTarget = (formTargetId: string, patch: Row) => {
    void (async () => {
      const next = classForms.map((entry) =>
        String(entry['formTargetId']) === formTargetId ? { ...entry, ...patch } : entry,
      );
      setClassForms(next);
      try {
        await dialogs.processing.wrap(dataSource.update(pageId, classId, { classForms: next }));
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 存檔（validForSave :2177-2208＋doSave :2209-2317）
  // ------------------------------------------------------------------
  const save = () => {
    void (async () => {
      // legacy `$('#inputForm').valid()`：required 欄位 inline 檢核（不過不送，無 alert 文字）。
      // 注意（鏡像 legacy 語意）：醫院/主要訓練類別/部門三欄的「未選」在 legacy 是 -1 哨兵
      // （select 預設選項，jquery validate 視為非空、由 validForSave 攔）——React 的 placeholder
      // 空值等價於該哨兵，因此這三欄不在 requiredMissing，由下方 validForSave 出專屬文案。
      const requiredMissing =
        core.name.trim() === '' ||
        core.courseTypeId === '' ||
        core.caseType === '' ||
        (core.classDate.trim() === '' && core.classDates.trim() === '') ||
        core.beginTime.trim() === '' ||
        core.endTime.trim() === '' ||
        core.attendanceQty.trim() === '' ||
        core.qty.trim() === '' ||
        core.locationId === '' ||
        core.formOwnerId === '';
      if (requiredMissing) {
        // inline jquery validate 鏡像：不送出（批次 10 證照 modal 同先例，不顯示 alert 文字）。
        return;
      }
      // validForSave（:2177-2208）：errMsg 以 <br> 串接 → React 以換行呈現。
      const errMessages: string[] = [];
      // isAdminAccount：雛形無權限模型，視為 admin（刪除鈕顯示同語意）。
      if (core.organizationId === '') {
        errMessages.push('請選擇醫院'); // :2181（無標點）
      }
      if (core.jobTypeId === '') {
        errMessages.push('請選擇訓練類別'); // :2185
      }
      if (core.departmentId === '') {
        errMessages.push('請選擇部門'); // :2188
      }
      const hasInstructor = teachers.some((entry) => entry.teacherType === 'instructor');
      if (!hasInstructor) {
        errMessages.push('至少選擇一名授課教師'); // :2193
      }
      if (core.endTime !== '' && core.beginTime !== '' && core.endTime < core.beginTime) {
        errMessages.push('請輸入正確的起訖區間!'); // :2199（0021；legacy isBefore 嚴格小於——相等合法）
      }
      if (errMessages.length > 0) {
        await dialogs.alert(
          <span>
            {errMessages.map((message) => (
              <span key={message} className="block">
                {message}
              </span>
            ))}
          </span>,
        );
        return;
      }
      const attendanceRole = ATTENDANCE_ROLES.filter((role) => roleChecks.has(role.value))
        .map((role) => role.value)
        .join(','); // :2221-2229
      const instructor = teachers.find((entry) => entry.teacherType === 'instructor');
      const refJobType = core.refJobTypeIds.filter((id) => id !== core.jobTypeId); // :2246-2261 去主類別
      const values: Row = {
        ...core,
        weeks: WEEK_VALUES.filter((_, index) => core.weeks[index]),
        attendanceRole,
        refJobTypeId: refJobType.join(','),
        actionType: core.actionTypes.join(','),
        deviceList: core.devices.join(','),
        instructorId: instructor?.id ?? '',
        studentIds: students.map((entry) => entry.id), // :2229-2236 JSON.stringify 的鏡像
        classTeacherList: teachers.map((entry) => ({
          teacherId: entry.id,
          teacherName: entry.name,
          type: entry.teacherType === 'instructor' ? 1 : 2,
        })),
        attachFiles,
        attendanceList: students.map((entry) => ({
          studentId: entry.id,
          studentName: entry.name.replace(/\(.*\)$/, ''),
        })),
        // 表格欄鏡像（legacy queryClass 由回傳欄位組表：課程代碼 id 不可寫＝列鍵）。
        title: core.name,
        unit: ACCOUNT_DEPARTMENT_OPTIONS.find((option) => option.value === core.departmentId)?.label ?? '',
        category:
          ACCOUNT_JOB_TYPE_OPTIONS.find((option) => option.value === core.jobTypeId)?.label ?? '',
        category2: COURSE_CLAZZ_KIND_OPTIONS.find((option) => option.value === core.kind)?.label ?? '',
        category3:
          COURSE_CLAZZ_COURSE_TYPE_OPTIONS.find((option) => option.value === core.courseTypeId)?.label ?? '',
        person: instructor?.name ?? '',
        date: core.classDate !== '' ? core.classDate : core.classDates,
      };
      setBusy(true);
      try {
        const saved =
          classId === '0'
            ? await dialogs.processing.wrap(dataSource.create(pageId, values))
            : await dialogs.processing.wrap(dataSource.update(pageId, classId, values));
        setClassId(String(saved['id'] ?? classId)); // :2244-2251 重開 modal（雛形：id 回填＋停留）
        await dialogs.info(saveSuccessText()); // :2306 showInfoDialog(response.desc)
        // 多筆排課（response.data undefined）才關 modal（:2298-2303）；雛形恆有 data → 停留。
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  /** modal 內刪除（delClass('modal') :358-386）。 */
  const deleteClass = () => {
    void (async () => {
      const confirmed = await dialogs.confirm('是否確定要刪除？'); // :366（全形？FF1F）
      if (!confirmed) {
        return;
      }
      setBusy(true);
      try {
        await dialogs.processing.wrap(dataSource.remove(pageId, classId));
        closeMainModal(); // :373-375 modal('hide')＋queryClass
        await dialogs.info(saveSuccessText());
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 畫面
  // ------------------------------------------------------------------
  // 批次 50（工作項 4）：key（payload key 語意）入 uiId/htmlFor 的字尾一律 toUiKey kebab 化
  // （§10.3 規則 1；key 參數本身不動＝payload 域零影響）。
  const fieldLabel = (key: string, text: string, required: boolean) => (
    <label className="shrink-0 text-sm" htmlFor={`${pageId}-class-field-${toUiKey(key)}`}>
      {required ? <span className="text-danger">*</span> : null}
      {text}
    </label>
  );

  const textInput = (
    key: string,
    value: string,
    onChange: (next: string) => void,
    options?: { maxLength?: number; readOnly?: boolean },
  ) => (
    <TextInput
      uiId={`${pageId}-class-field-${toUiKey(key)}`}
      value={value}
      onChange={onChange}
      {...(options?.maxLength !== undefined ? { maxLength: options.maxLength } : {})}
      {...(options?.readOnly === true ? { readOnly: true } : {})}
    />
  );

  const multiSelect = (
    key: string,
    values: readonly string[],
    options: readonly { value: string; label: string }[],
    onChange: (next: readonly string[]) => void,
  ) => (
    <select
      data-ui-id={`${pageId}-class-field-${toUiKey(key)}`}
      multiple
      className="h-20 rounded border border-border px-1 text-sm"
      value={[...values]}
      onChange={(event) => {
        const picked = Array.from(event.target.selectedOptions).map((option) => option.value);
        onChange(picked);
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  const checkbox = (key: string, checked: boolean, onChange: (next: boolean) => void, text: string) => (
    <label className="inline-flex items-center gap-1 text-sm">
      <input
        data-ui-id={`${pageId}-class-field-${toUiKey(key)}`}
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      {text}
    </label>
  );

  const formColumns: readonly TableColumn[] = [
    { key: 'action', header: '', align: 'center' },
    { key: 'formName', header: '表單名稱', align: 'center' }, // :878
    { key: 'doneDateDiff', header: '應完成日(結束後)', align: 'center' }, // :879-880
    { key: 'students', header: '填寫者', align: 'center' }, // :881
    { key: 'dispatchType', header: '與會人員必填', align: 'center' }, // :882-883
    { key: 'isParticipantRead', header: '可查看同學的記錄', align: 'center' }, // :884-885
  ];

  return (
    <ModalFrame
      uiId={`${pageId}-class-dialog`}
      open
      title={mode === 'create' ? '新增課程' : '修改課程'} // clazzList_js.jsp:17-18（dialogTitleNew/Update）
      size="lg"
      onClose={closeMainModal}
      footer={
        <>
          <Button
            uiId={`${pageId}-class-dialog-footer-close`}
            variant="default"
            size="sm"
            type="button"
            onClick={closeMainModal}
          >
            關閉 {/* :853 */}
          </Button>
          {mode === 'edit' ? (
            <Button
              uiId={`${pageId}-class-dialog-delete`}
              variant="danger"
              size="sm"
              type="button"
              disabled={busy}
              dataEffect={`remove:${pageId}`}
              onClick={deleteClass}
            >
              刪除 {/* :856-860 delClassBtn（新增模式 hide :549-551） */}
            </Button>
          ) : null}
          <Button
            uiId={`${pageId}-class-dialog-save`}
            variant="primary"
            size="sm"
            type="button"
            disabled={busy}
            dataEffect={`${classId === '0' ? 'create' : 'update'}:${pageId}`}
            onClick={save}
          >
            課程存檔 {/* :861-865 */}
          </Button>
        </>
      }
    >
      <div data-ui-id={`${pageId}-class-dialog-body`} className="space-y-3">
        {/* 主表單（clazzList.jsp:227-844）。 */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            {fieldLabel('kind', '類型：', true)}
            <Select
              uiId={`${pageId}-class-field-kind`}
              value={core.kind}
              onChange={(next) => {
                setCoreValue('kind', next);
              }}
              options={[...COURSE_CLAZZ_KIND_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('organizationId', '醫院：', true)}
            <Select
              uiId={`${pageId}-class-field-organization-id`}
              value={core.organizationId}
              onChange={(next) => {
                setCoreValue('organizationId', next);
              }}
              placeholder="請選擇"
              options={[...ACCOUNT_ORGANIZATION_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('jobTypeId', '主要訓練類別：', true)}
            <Select
              uiId={`${pageId}-class-field-job-type-id`}
              value={core.jobTypeId}
              onChange={(next) => {
                setCoreValue('jobTypeId', next);
              }}
              placeholder="請選擇"
              options={[...ACCOUNT_JOB_TYPE_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('refJobTypeId', '其他參與訓練類別：', false)}
            {multiSelect('refJobTypeId', core.refJobTypeIds, [...ACCOUNT_JOB_TYPE_OPTIONS], (next) => {
              setCoreValue('refJobTypeIds', next);
            })}
          </div>
          <div className="col-span-2">
            {checkbox('isCrossDomain', core.isCrossDomain, (next) => {
              setCoreValue('isCrossDomain', next);
            }, '※允許跨職類上課(勾選後各訓練類別皆可查詢)')}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('departmentId', '部門：', true)}
            <Select
              uiId={`${pageId}-class-field-department-id`}
              value={core.departmentId}
              onChange={(next) => {
                setCoreValue('departmentId', next);
              }}
              placeholder="請選擇"
              options={[...ACCOUNT_DEPARTMENT_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('courseDeptId', '課程樣版：', false)}
            <Select
              uiId={`${pageId}-class-field-course-dept-id`}
              value={core.courseDeptId}
              onChange={(next) => {
                setCoreValue('courseDeptId', next);
              }}
              placeholder="請選擇"
              options={[...ACCOUNT_DEPARTMENT_OPTIONS]}
            />
            <Select
              uiId={`${pageId}-class-field-course-id`}
              value={core.courseId}
              onChange={(next) => {
                setCoreValue('courseId', next);
              }}
              placeholder="請選擇"
              options={[...COURSE_CLAZZ_TEMPLATE_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('courseTypeId', '課程類別：', true)}
            <Select
              uiId={`${pageId}-class-field-course-type-id`}
              value={core.courseTypeId}
              onChange={(next) => {
                setCoreValue('courseTypeId', next);
              }}
              placeholder="請選擇"
              options={[...COURSE_CLAZZ_COURSE_TYPE_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('caseType', '必/選修：', true)}
            <Select
              uiId={`${pageId}-class-field-case-type`}
              value={core.caseType}
              onChange={(next) => {
                setCoreValue('caseType', next);
              }}
              options={[...COURSE_CLAZZ_CASE_TYPE_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('code', '課程代碼：', false)}
            {/* :339-341 課程代碼是 label；新增＝由系統自動產生（clazzList_js.jsp:505，F3 行號更正）、
                修改＝currentRow.code（:521）——雛形 code 未存列上，以列鍵 id 顯示【雛形值待實機校準】。 */}
            <span data-ui-id={`${pageId}-class-field-code`} className="text-sm">
              {mode === 'edit' && classId !== '0'
                ? String(row?.['id'] ?? '')
                : '由系統自動產生'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('name', '課程名稱：', true)}
            {textInput('name', core.name, (next) => {
              setCoreValue('name', next);
            }, { maxLength: 50 })}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('classDate', '課程日期：', true)}
            {/* newShow=classDates（multi，新增）/newHide=classDate（datepicker，修改）；:361-373 */}
            {mode === 'create'
              ? textInput('classDates', core.classDates, (next) => {
                  setCoreValue('classDates', next);
                }, { readOnly: false })
              : textInput('classDate', core.classDate, (next) => {
                  setCoreValue('classDate', next);
                })}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('beginTime', '課程時間：', true)}
            {textInput('beginTime', core.beginTime, (next) => {
              setCoreValue('beginTime', next);
            })}
            {textInput('endTime', core.endTime, (next) => {
              setCoreValue('endTime', next);
            })}
          </div>
          <div className="col-span-2">
            {/* 循環排課（:385-397，新增模式；legacy newShow）。 */}
            {mode === 'create' ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm">循環排課：</span>
                {WEEK_LABELS.map((label, index) =>
                  checkbox(
                    `week${index}`,
                    core.weeks[index] ?? false,
                    (next) => {
                      const nextWeeks = [...core.weeks];
                      nextWeeks[index] = next;
                      setCoreValue('weeks', nextWeeks);
                    },
                    label,
                  ),
                )}
                <span className="text-xs text-gray-500">(↑請選2個課程日期為循環開課的起訖區間)</span>
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('isSignUp', '開放報名：', true)}
            <label className="inline-flex items-center gap-1 text-sm">
              <input
                data-ui-id={`${pageId}-class-field-is-signup1`}
                type="radio"
                name={`${pageId}-class-isSignUp`}
                checked={core.isSignUp === '1'}
                onChange={() => {
                  setCoreValue('isSignUp', '1');
                }}
              />
              是
            </label>
            <label className="inline-flex items-center gap-1 text-sm">
              <input
                data-ui-id={`${pageId}-class-field-is-signup0`}
                type="radio"
                name={`${pageId}-class-isSignUp`}
                checked={core.isSignUp === '0'}
                onChange={() => {
                  setCoreValue('isSignUp', '0');
                }}
              />
              否
            </label>
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('signUpPeriod', '報名期間：', true)}
            {textInput('signUpPeriod', core.signUpPeriod, (next) => {
              setCoreValue('signUpPeriod', next);
            })}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('attendanceQty', '學員人數上限：', true)}
            {textInput('attendanceQty', core.attendanceQty, (next) => {
              setCoreValue('attendanceQty', next);
            })}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('qty', '給予時數：', true)}
            {textInput('qty', core.qty, (next) => {
              setCoreValue('qty', next);
            })}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('locationId', '地點：', true)}
            <Select
              uiId={`${pageId}-class-field-location-dept-id`}
              value={core.locationDeptId}
              onChange={(next) => {
                setCoreValue('locationDeptId', next);
              }}
              placeholder="請選擇"
              options={[...COURSE_CLAZZ_LOCATION_DEPT_OPTIONS]}
            />
            <Select
              uiId={`${pageId}-class-field-location-id`}
              value={core.locationId}
              onChange={(next) => {
                setCoreValue('locationId', next);
              }}
              placeholder="請選擇"
              options={[...COURSE_CLAZZ_LOCATION_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('teacherId', '教師：', false)}
            <Select
              uiId={`${pageId}-class-field-teacher-dept-id`}
              value={core.teacherDeptId}
              onChange={(next) => {
                setCoreValue('teacherDeptId', next);
              }}
              placeholder="請選擇"
              options={[...ACCOUNT_DEPARTMENT_OPTIONS]}
            />
            <Select
              uiId={`${pageId}-class-field-teacher-id`}
              value={core.teacherId}
              onChange={(next) => {
                setCoreValue('teacherId', next);
              }}
              placeholder="請選擇教師"
              options={[...COURSE_CLAZZ_TEACHER_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              uiId={`${pageId}-class-add-instructor`}
              variant="primary"
              size="sm"
              type="button"
              onClick={() => {
                addTeacher('instructor');
              }}
            >
              新增授課教師 {/* :512-514 */}
            </Button>
            <Button
              uiId={`${pageId}-class-add-team-teacher`}
              variant="default"
              size="sm"
              type="button"
              onClick={() => {
                addTeacher('teamTeacher');
              }}
            >
              新增協同教師 {/* :518-520 */}
            </Button>
          </div>
          <div className="col-span-2">
            <span data-ui-id={`${pageId}-class-teacher-label`} className="text-sm">
              教師清單: {/* :538-540 */}
            </span>
            <span data-ui-id={`${pageId}-class-teacher-area`} className="ml-2 inline-flex flex-wrap gap-2">
              {teachers.map((entry) => (
                <span
                  key={`${entry.teacherType}-${entry.id}`}
                  data-ui-id={`${pageId}-class-teacher-${entry.teacherType}-${entry.id}`}
                  className="inline-flex items-center gap-1 rounded border border-border px-1 text-sm"
                >
                  {entry.name}
                  <span className="text-xs text-gray-500">
                    {entry.teacherType === 'instructor' ? '授課教師' : '協同教師'}
                  </span>
                  <button
                    data-ui-id={`${pageId}-class-teacher-remove-${entry.teacherType}-${entry.id}`}
                    type="button"
                    className="font-bold text-red-500"
                    onClick={() => {
                      removeTeacher(entry.id, entry.teacherType);
                    }}
                  >
                    x
                  </button>
                </span>
              ))}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('visiting-instructor', '外部講師：', false)}
            {/* :543 placeholder="講師姓名"。 */}
            <input
              data-ui-id={`${pageId}-class-field-visiting-instructor`}
              type="text"
              maxLength={50}
              placeholder="講師姓名"
              className="rounded border border-border px-2 py-1 text-sm"
              value={core.visitingInstructor}
              onChange={(event) => {
                setCoreValue('visitingInstructor', event.target.value);
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('formOwnerId', '課程管理人員：', true)}
            <Select
              uiId={`${pageId}-class-field-form-owner-dept-id`}
              value={core.formOwnerDeptId}
              onChange={(next) => {
                setCoreValue('formOwnerDeptId', next);
              }}
              placeholder="請選擇"
              options={[...ACCOUNT_DEPARTMENT_OPTIONS]}
            />
            <Select
              uiId={`${pageId}-class-field-form-owner-id`}
              value={core.formOwnerId}
              onChange={(next) => {
                setCoreValue('formOwnerId', next);
              }}
              placeholder="請選擇"
              options={[...COURSE_CLAZZ_TEACHER_OPTIONS]}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('actionType', '評核方式(複選)：', false)}
            {multiSelect('actionType', core.actionTypes, [...COURSE_CLAZZ_ACTION_TYPE_OPTIONS], (next) => {
              setCoreValue('actionTypes', next);
            })}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('deviceList', '設備(複選)：', false)}
            {multiSelect('deviceList', core.devices, [...COURSE_CLAZZ_DEVICE_OPTIONS], (next) => {
              setCoreValue('devices', next);
            })}
          </div>
        </div>

        {/* 課程教材上傳（:600-640）。 */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm">課程教材上傳:</span>
            <label className="text-sm" htmlFor={`${pageId}-class-attach-input`}>
              <Button
                uiId={`${pageId}-class-attach-add`}
                variant="primary"
                size="sm"
                type="button"
                onClick={() => {
                  if (newAttachName.trim() !== '') {
                    addAttachFile(newAttachName);
                  }
                }}
              >
                新增檔案 {/* :608-610 */}
              </Button>
            </label>
            <input
              data-ui-id={`${pageId}-class-attach-input`}
              type="text"
              className="rounded border border-border px-2 py-1 text-sm"
              placeholder="檔案名稱（雛形不落地【雛形值待實機校準】）"
              value={newAttachName}
              onChange={(event) => {
                setNewAttachName(event.target.value);
              }}
            />
          </div>
          <Table
            uiId={`${pageId}-class-attach-table`}
            columns={[
              { key: 'name', header: '檔案名稱', align: 'center' }, // :623
              { key: 'size', header: '檔案大小', align: 'center' }, // :624
              { key: 'action', header: '', align: 'center' },
            ]}
            rows={attachFiles as readonly TableRow[]}
            rowKey={(entry) => String(entry['fileId'])}
            emptyText="查無符合的資料"
            renderCell={(entry, columnKey) => {
              if (columnKey === 'name') {
                return String(entry['name'] ?? '');
              }
              if (columnKey === 'size') {
                return String(entry['size'] ?? '');
              }
              return (
                <span className="inline-flex gap-1">
                  <Button
                    uiId={`${pageId}-class-attach-download-${String(entry['fileId'])}`}
                    variant="default"
                    size="sm"
                    type="button"
                    onClick={() => {
                      void dialogs.info('檔案下載（雛形不落地【雛形值待實機校準】）');
                    }}
                  >
                    下載
                  </Button>
                  <Button
                    uiId={`${pageId}-class-attach-delete-${String(entry['fileId'])}`}
                    variant="danger"
                    size="sm"
                    type="button"
                    onClick={() => {
                      deleteAttachFile(String(entry['fileId']));
                    }}
                  >
                    刪除
                  </Button>
                </span>
              );
            }}
          />
        </div>

        {/* 教學內容（:645-655，CKEDITOR→textarea）。 */}
        <div className="flex items-start gap-2">
          {fieldLabel('test-content', '教學內容：', false)}
          <textarea
            data-ui-id={`${pageId}-class-field-test-content`}
            rows={8}
            className="flex-1 border border-border px-2 py-1 text-sm"
            placeholder="內容（CKEDITOR 雛形以純文字呈現【雛形值待實機校準】）"
            value={core.testContent}
            onChange={(event) => {
              setCoreValue('testContent', event.target.value);
            }}
          />
        </div>

        {/* 新增與會人員（:657-671 四 radio＋:672-832 四區塊）。 */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm">新增與會人員：</span>
            {[
              { value: '2', label: '依計畫挑選人員' },
              { value: '0', label: '匯入名單' },
              { value: '1', label: '增加人員' },
              { value: '4', label: '依角色' },
            ].map((option) => (
              <label key={option.value} className="inline-flex items-center gap-1 text-sm">
                <input
                  data-ui-id={`${pageId}-class-add-member-type${option.value}`}
                  type="radio"
                  name={`${pageId}-class-addMemberType`}
                  checked={addMemberType === option.value}
                  onChange={() => {
                    setAddMemberType(option.value); // changeAddMemberType（:2606-2637）
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
          {addMemberType === '0' ? (
            <div data-ui-id={`${pageId}-class-import-member`} className="flex items-center gap-2">
              <span className="text-sm">匯入人員：</span>
              <input
                data-ui-id={`${pageId}-class-member-file`}
                type="file"
                className="text-sm"
                onChange={(event) => {
                  setImportFileName(event.target.files?.[0]?.name ?? '');
                }}
              />
              <Button
                uiId={`${pageId}-class-member-import`}
                variant="primary"
                size="sm"
                type="button"
                onClick={importMemberList}
              >
                匯入 {/* :684 */}
              </Button>
              <Button
                uiId={`${pageId}-class-member-template`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  // downloadTemplate('課程管理匯入與會人員名單.xlsx')（:685）；批次 9 Blob 下載先例。
                  const blob = new Blob(['課程管理匯入與會人員名單（雛形值待實機校準）'], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const anchor = document.createElement('a');
                  anchor.href = url;
                  anchor.download = '課程管理匯入與會人員名單.xlsx';
                  anchor.click();
                  URL.revokeObjectURL(url);
                }}
              >
                範本下載 {/* :685 */}
              </Button>
            </div>
          ) : null}
          {addMemberType === '1' ? (
            <div data-ui-id={`${pageId}-class-add-member`} className="flex flex-wrap items-center gap-2">
              <span className="text-sm">新增人員(複選)：</span>
              <input
                data-ui-id={`${pageId}-class-account-keyword`}
                type="text"
                className="rounded border border-border px-2 py-1 text-sm"
                placeholder="(請輸入姓名或員工編號)"
                value={accountKeyword}
                onChange={(event) => {
                  setAccountKeyword(event.target.value);
                }}
              />
              <Select
                uiId={`${pageId}-class-field-account-id`}
                value={accountIdSelected}
                onChange={setAccountIdSelected}
                placeholder="請選擇"
                options={accountIdOptions}
              />
              <Button
                uiId={`${pageId}-class-add-any-student`}
                variant="primary"
                size="sm"
                type="button"
                onClick={addAnyStudent}
              >
                加入 {/* :699 */}
              </Button>
            </div>
          ) : null}
          {addMemberType === '4' ? (
            <div data-ui-id={`${pageId}-class-add-role`} className="flex flex-wrap items-center gap-2">
              <span className="text-sm">依角色選取(存檔後才會帶入人員)：</span>
              {ATTENDANCE_ROLES.map((role) => (
                <label key={role.value} className="inline-flex items-center gap-1 text-sm">
                  <input
                    data-ui-id={`${pageId}-class-role-chk-${role.value}`}
                    type="checkbox"
                    checked={roleChecks.has(role.value)}
                    onChange={(event) => {
                      const next = new Set(roleChecks);
                      if (event.target.checked) {
                        next.add(role.value);
                      } else {
                        next.delete(role.value);
                      }
                      setRoleChecks(next);
                    }}
                  />
                  {role.label}
                </label>
              ))}
              <Button
                uiId={`${pageId}-class-role-select-all`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  selectAllRole(true); // :2757-2760
                }}
              >
                選擇全部
              </Button>
              <Button
                uiId={`${pageId}-class-role-select-none`}
                variant="danger"
                size="sm"
                type="button"
                onClick={() => {
                  selectAllRole(false); // :2761-2764
                }}
              >
                取消全部
              </Button>
            </div>
          ) : null}
          {addMemberType === '2' ? (
            <div data-ui-id={`${pageId}-class-add-plan-member`} className="space-y-1">
              {/* :722 區塊標題（F2 補）。 */}
              <div className="text-sm">新增學員(計劃)：</div>
              <div className="grid grid-cols-3 gap-2">
              <Select
                uiId={`${pageId}-class-field-stu-annual-id`}
                value={planForm.stuAnnualId}
                onChange={(next) => {
                  setPlanForm((current) => ({ ...current, stuAnnualId: next }));
                }}
                placeholder="請選擇年度"
                options={[...COURSE_CLAZZ_ANNUAL_OPTIONS]}
              />
              <Select
                uiId={`${pageId}-class-field-stu-job-type-id`}
                value={planForm.stuJobTypeId}
                onChange={(next) => {
                  setPlanForm((current) => ({ ...current, stuJobTypeId: next }));
                }}
                placeholder="請選擇"
                options={[...ACCOUNT_JOB_TYPE_OPTIONS]}
              />
              <Select
                uiId={`${pageId}-class-field-stu-department-id`}
                value={planForm.stuDepartmentId}
                onChange={(next) => {
                  setPlanForm((current) => ({ ...current, stuDepartmentId: next }));
                }}
                placeholder="請選擇"
                options={[...ACCOUNT_DEPARTMENT_OPTIONS]}
              />
              <Select
                uiId={`${pageId}-class-field-plan-target-id`}
                value={planForm.planTargetId}
                onChange={(next) => {
                  setPlanForm((current) => ({ ...current, planTargetId: next }));
                }}
                placeholder="請選擇"
                options={[...COURSE_CLAZZ_PLAN_TARGET_OPTIONS]}
              />
              <Select
                uiId={`${pageId}-class-field-phase-dept-id`}
                value={planForm.phaseDeptId}
                onChange={(next) => {
                  setPlanForm((current) => ({ ...current, phaseDeptId: next }));
                }}
                placeholder="請選擇"
                options={[...COURSE_CLAZZ_PHASE_OPTIONS]}
              />
              <div className="flex items-center gap-2">
                <span className="text-sm">訓練月份：</span>
                <TextInput
                  uiId={`${pageId}-class-field-phase-ym`}
                  value={planForm.phaseYM}
                  onChange={(next) => {
                    setPlanForm((current) => ({ ...current, phaseYM: next }));
                  }}
                />
              </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* 學員雙 select（:794-832）。 */}
        <div className="space-y-1">
          <span className="text-sm">學員清單：</span>
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
            <div>
              <div className="text-center text-sm">學員</div>
              <select
                data-ui-id={`${pageId}-class-student-pool`}
                multiple
                size={8}
                className="w-full rounded border border-border px-1 text-sm"
                value={[...pickedPool]}
                onChange={(event) => {
                  setPickedPool(Array.from(event.target.selectedOptions).map((option) => option.value));
                }}
              >
                {studentPool.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 pt-6">
              <Button
                uiId={`${pageId}-class-student-right-all`}
                variant="default"
                size="sm"
                type="button"
                onClick={moveStudentsRightAll}
              >
                ⟩⟩
              </Button>
              <Button
                uiId={`${pageId}-class-student-right-selected`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  moveSelectedRight(pickedPool);
                }}
              >
                ⟩
              </Button>
              <Button
                uiId={`${pageId}-class-student-left-selected`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  moveSelectedLeft(pickedTo);
                }}
              >
                ⟨
              </Button>
              <Button
                uiId={`${pageId}-class-student-left-all`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  moveSelectedLeft(students.map((entry) => entry.id));
                }}
              >
                ⟨⟨
              </Button>
            </div>
            <div>
              <div className="text-center text-sm">上課學員</div>
              <select
                data-ui-id={`${pageId}-class-student-to`}
                multiple
                size={8}
                className="w-full rounded border border-border px-1 text-sm"
                value={[...pickedTo]}
                onChange={(event) => {
                  setPickedTo(Array.from(event.target.selectedOptions).map((option) => option.value));
                }}
              >
                {students.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 表單控制（:866-925；edit 模式自動開 :615-616）。 */}
        <div className="space-y-1">
          <Button
            uiId={`${pageId}-class-form-control-toggle`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              setFormControlOpen((current) => !current); // showFormControl :2703-2710 slideToggle
            }}
          >
            表單控制
          </Button>
          {formControlOpen ? (
            <div data-ui-id={`${pageId}-class-form-control`} className="space-y-1">
              <span className="block bg-yellow-100 px-2 py-1 text-sm">表單控制</span>
              <Table
                uiId={`${pageId}-class-forms-table`}
                columns={formColumns}
                rows={classForms as readonly TableRow[]}
                rowKey={(entry) => String(entry['formTargetId'])}
                emptyText="查無符合的資料"
                renderCell={(entry, columnKey) => {
                  const formTargetId = String(entry['formTargetId']);
                  if (columnKey === 'action') {
                    return (
                      <Button
                        uiId={`${pageId}-class-form-delete-${formTargetId}`}
                        variant="danger"
                        size="sm"
                        type="button"
                        dataEffect={`update:${pageId}`}
                        onClick={() => {
                          deleteFormTarget(formTargetId);
                        }}
                      >
                        刪除
                      </Button>
                    );
                  }
                  if (columnKey === 'formName') {
                    return String(entry['formName'] ?? '');
                  }
                  if (columnKey === 'doneDateDiff') {
                    return (
                      <TextInput
                        uiId={`${pageId}-class-form-done-date-diff-${formTargetId}`}
                        value={String(entry['doneDateDiff'] ?? '')}
                        onChange={(next) => {
                          patchFormTarget(formTargetId, { doneDateDiff: next });
                        }}
                      />
                    );
                  }
                  if (columnKey === 'students') {
                    const formList = Array.isArray(entry['formList'])
                      ? (entry['formList'] as readonly Row[])
                      : [];
                    return (
                      <span className="inline-flex flex-wrap gap-1">
                        {students.map((student) => {
                          const checked = formList.some(
                            (form) =>
                              String(form['studentId']) === student.id &&
                              (form['enable'] === true || String(form['enable']) === 'true'),
                          );
                          return (
                            <label key={student.id} className="inline-flex items-center gap-1 text-sm">
                              <input
                                data-ui-id={`${pageId}-class-form-student-${formTargetId}-${student.id}`}
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  // studentFormChange（:2361-2410）：勾＝saveFormForStudent、
                                  // 取消＝deleteFormForStudent；雛形改列上 formList 即寫回。
                                  const nextFormList = event.target.checked
                                    ? [...formList, { studentId: student.id, enable: true }]
                                    : formList.filter((form) => String(form['studentId']) !== student.id);
                                  patchFormTarget(formTargetId, { formList: nextFormList });
                                }}
                              />
                              {student.name}
                            </label>
                          );
                        })}
                      </span>
                    );
                  }
                  if (columnKey === 'dispatchType') {
                    return (
                      <input
                        data-ui-id={`${pageId}-class-form-dispatch-type-${formTargetId}`}
                        type="checkbox"
                        checked={String(entry['dispatchType']) === '0'}
                        onChange={(event) => {
                          // formDispatchTypeChange（:2711-2738）：checked→0。
                          patchFormTarget(formTargetId, { dispatchType: event.target.checked ? 0 : 1 });
                        }}
                      />
                    );
                  }
                  if (columnKey === 'isParticipantRead') {
                    return (
                      <input
                        data-ui-id={`${pageId}-class-form-participant-read-${formTargetId}`}
                        type="checkbox"
                        checked={Number(entry['isParticipantRead'] ?? 0) > 0}
                        onChange={(event) => {
                          // formParticipantReadChange（:2411-2431）。
                          patchFormTarget(formTargetId, {
                            isParticipantRead: event.target.checked ? 1 : 0,
                          });
                        }}
                      />
                    );
                  }
                  return '\u00a0';
                }}
              />
              {/* 新增表單列（:887-923）。 */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  uiId={`${pageId}-class-form-save`}
                  variant="primary"
                  size="sm"
                  type="button"
                  dataEffect={`update:${pageId}`}
                  onClick={formSave}
                >
                  新增 {/* :892 formSave */}
                </Button>
                <Select
                  uiId={`${pageId}-class-form-dept-id`}
                  value={formDeptId}
                  onChange={setFormDeptId}
                  placeholder="請選擇"
                  options={[...ACCOUNT_DEPARTMENT_OPTIONS]}
                />
                <Select
                  uiId={`${pageId}-class-form-template`}
                  value={formTemplateId}
                  onChange={setFormTemplateId}
                  placeholder="請選擇"
                  options={[...COURSE_CLAZZ_FORM_TEMPLATE_OPTIONS]}
                />
                {/* :892-893 legacy 新增列無 label（th 已有），input＋日＋兩 checkbox（:894-901）。 */}
                <TextInput
                  uiId={`${pageId}-class-form-done-date-diff-new`}
                  value={formDoneDateDiff}
                  onChange={setFormDoneDateDiff}
                />
                <span className="text-sm">日</span>
                {checkbox('form-dispatchType-new', formDispatchType, setFormDispatchType, '與會人員必填')}
                {checkbox('form-participantRead-new', formParticipantRead, setFormParticipantRead, '可查看同學的記錄')}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </ModalFrame>
  );
}
